import { ErrorHandler, LocalizedDateTimeFromUtc, LocalizedMinorHighlight, OverlayedLoader } from '@mews-commander/ui';
import { WithBillId } from '@mews-commander/types';
import { Body, Stack } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { UnicodeEntity } from '@mews/enums';
import { useAccountsByOwnerId } from '../../hooks';
import { SidePanelInfoRowItem } from '../SidePanelInfoRowItem';
import { useCorrectionDetail } from '../../hooks/useCorrectionDetail';
import { CorrectionBillCustomerInfo } from './CorrectionBillCustomerInfo';
import { CorrectionBillCorrectionType } from './CorrectionBillCorrectionType';
import { CorrectionBillOrderItemsTable } from './CorrectionBillOrderItemsTable';
import { CorrectionBillCorrectedBillLink } from './CorrectionBillCorrectedBillLink';

export const CorrectionBillDetailContent = ({ billId }: WithBillId) => {
    const correctionQuery = useCorrectionDetail({ correctionBillId: billId });
    const accountsQuery = useAccountsByOwnerId(correctionQuery.data?.correctiveBill?.ownerId);
    const { correctiveBill, owner, correctedBill, creatorProfile } = correctionQuery.data || {};

    const isCompany = !!accountsQuery?.data?.companies?.length;

    return (
        <>
            <Stack vertical spacing="200">
                <LocalizedMinorHighlight textKey={TextKey.DetailPlural} />
                {(accountsQuery.isFetching || correctionQuery.isFetching) && <OverlayedLoader fixed show />}
                {(accountsQuery.isError || correctionQuery.isError) && <ErrorHandler />}
                {correctionQuery.isSuccess && accountsQuery.isSuccess && correctiveBill && (
                    <Stack vertical>
                        <CorrectionBillCustomerInfo ownerId={owner?.id} ownerName={owner?.name} isCompany={isCompany} />

                        <SidePanelInfoRowItem
                            rowLabelTextKey={TextKey.CorrectionDate}
                            rowInfo={
                                <Body>
                                    <LocalizedDateTimeFromUtc utcString={correctiveBill.createdUtc} />
                                </Body>
                            }
                        />
                        <SidePanelInfoRowItem
                            rowLabelTextKey={TextKey.CorrectedBy}
                            rowInfo={<Body>{creatorProfile?.name || UnicodeEntity.Dash}</Body>}
                        />
                        <SidePanelInfoRowItem
                            rowLabelTextKey={TextKey.CorrectedBill}
                            rowInfo={
                                <Body>
                                    {correctedBill ? (
                                        <CorrectionBillCorrectedBillLink bill={correctedBill} />
                                    ) : (
                                        UnicodeEntity.Dash
                                    )}
                                </Body>
                            }
                        />
                        <CorrectionBillCorrectionType correctionType={correctiveBill.correctionType} />
                        <SidePanelInfoRowItem
                            rowLabelTextKey={TextKey.Reason}
                            rowInfo={<Body>{correctiveBill.reason || UnicodeEntity.Dash}</Body>}
                            hideDivider
                        />
                    </Stack>
                )}
                <CorrectionBillOrderItemsTable billId={billId} />
            </Stack>
        </>
    );
};
